import request from '../utils/request';

export interface PdaUser {
  token: string;
  username?: string;
  full_name?: string;
  emp_no?: string;
  company_code?: string;
  company_id?: number;
  company_name?: string;
  role_code?: string;
  role_name?: string;
  [key: string]: unknown;
}

export interface PdaCompany {
  id: number;
  name: string;
  code?: string;
  role_code?: string;
  role_name?: string;
  status?: number;
}

export interface MyCompaniesData {
  current_company_id?: number;
  companies: PdaCompany[];
}

export interface SwitchOrganizationData {
  token: string;
  company: PdaCompany;
  role_code?: string;
  role_name?: string;
}

export interface ScanInboundSinglePayload {
  ocsf: string;
  loc_code: string;
  weight: number;
  note?: string;
}

export interface ScanInboundItemPayload {
  length: number;
  note?: string;
  ocsf: string;
  source_no?: string;
  source_type?: string;
  weight: number;
}

export interface ScanInboundBatchPayload {
  items: ScanInboundItemPayload[];
  loc_code: string;
}

export type ScanInboundPayload = ScanInboundBatchPayload | ScanInboundSinglePayload;

export interface ScanInboundItemResult {
  inbound_at?: string;
  inbound_length?: number;
  inbound_weight?: number;
  loc_code?: string;
  ocsf: string;
  offcut_stock_id?: number;
  status?: string;
  stock_log_id?: number;
  stock_length?: number;
  stock_weight?: number;
}

export interface ScanInboundResult {
  count?: number;
  inbound_at?: string;
  inbound_weight?: number;
  items?: ScanInboundItemResult[];
  loc_code?: string;
  ocsf?: string;
  offcut_stock_id?: number;
  status?: string;
  stock_log_id?: number;
  stock_weight?: number;
  total_count?: number;
  total_weight?: number;
}

export interface ScanOutboundPayload {
  ocsf: string;
  weight?: number;
  reason?: string;
  note?: string;
}

export interface ScanOutboundResult {
  offcut_stock_id: number;
  ocsf: string;
  from_loc?: string;
  weight_out: number;
  remaining_weight: number;
  status: string;
  outbound_at?: string;
  stock_log_id: number;
}

export interface ScanTransferPayload {
  ocsf: string;
  loc_code: string;
  note?: string;
}

export interface ScanTransferResult {
  offcut_stock_id: number;
  ocsf: string;
  from_loc?: string;
  to_loc: string;
  stock_weight: number;
  status: string;
  transfer_at?: string;
  stock_log_id: number;
}

/**
 * 头明细模型(2026-05-07):批次内单 SKU 行的概览。
 * PDA 扫单后让工人选 — 单 SKU 自动选,多 SKU 显示列表。
 */
export interface PreInboundItemSummary {
  id: number;
  sku_id: number;
  sku: string;
  contract_spec: string;
  smelt_heat_no: string;
  steel_mill_grade_raw: string;
  material_grade_attr: string;
  shape_category: string;
  heat_treat_code: string;
  heat_treat_abbr: string;
  total_pieces: number;
  total_weight: number;
  added_pieces: number;
  added_weight: number;
  remaining_pieces: number;
  status: string;
}

export interface PreInboundScanContext {
  id: number;
  sku: string;
  status: string;
  total_pieces: number;
  total_weight: number;
  added_pieces: number;
  added_weight: number;
  remaining_pieces: number;
  remaining_weight?: number;
  last_loc_code?: string;
  last_inspection_no?: string;
  inspection_batch_no?: string;
  inbound_batch_no?: string;
  supplier_id?: number | null;
  supplier_name?: string;
  material_category_id?: number | null;
  material_grade_id?: number | null;
  /** 头明细行(2026-05-07 起返回);老批次为 [] */
  items?: PreInboundItemSummary[];
}

export interface PreInboundDetail {
  id: number;
  inspection_batch_no?: string;
}

export interface PreInboundScanAddPayload {
  /** 头明细模型:本次录入的 SKU 行 ID。多 SKU 必传;单 SKU 不传 = 后端自动选 */
  pre_inbound_item_id?: number;
  loc_code: string;
  inbound_weight: number;
  inbound_length?: number;
  inspection_no?: string;
  note?: string;
  printer_id?: number;
}

export interface PreInboundScanAddResult {
  bid: string;
  batch_no: string;
  new_status: string;
  added_pieces: number;
  added_weight: number;
  remaining_pieces: number;
  print_job_id?: number;
}

export interface InventoryTraceBase {
  id: number;
  bid: string;
  sku: string;
  material_type: string;
  status: string;
  inbound_weight: number;
  avail_weight: number;
  inbound_length: number;
  batch_no: string;
  inspection_no: string;
  smelt_heat_no: string;
  industry_grade: string;
  supplier_id: number;
  supplier_name: string;
  material_cate_code: string;
  material_category: string;
  material_grade_code: string;
  material_grade: string;
  inbound_at: string;
  note: string;
  parent_bid?: string;
  child_bids?: string[];
}

export interface InventoryTraceLocation {
  loc_code: string;
  description: string;
  warehouse: string;
  warehouse_type: string;
  area: string;
}

export interface InventoryTraceOperator {
  id: number;
  name: string;
}

export interface InventoryTraceEvent {
  at: string;
  op_type: string;
  op_type_name: string;
  from_loc?: string;
  to_loc?: string;
  weight: number;
  operator?: InventoryTraceOperator;
  note?: string;
}

export interface InventoryTraceResponse {
  base: InventoryTraceBase;
  current_location: InventoryTraceLocation | null;
  timeline: InventoryTraceEvent[];
}

export interface OffcutTraceItem {
  op_at: string;
  op_type: string;
  from_loc?: string;
  to_loc?: string;
  weight?: number;
  op_by?: number;
  op_by_name?: string;
  operator_name?: string;
  op_detail?: string;
}

export interface OffcutTraceResult {
  offcut_stock_id: number;
  ocsf: string;
  sku?: string;
  status: string;
  current_location?: string;
  inbound_weight?: number;
  outbound_weight?: number;
  stock_weight?: number;
  inbound_at?: string;
  timeline: OffcutTraceItem[];
}

export type ProductionTaskStatus = 'pending' | 'claimed' | 'in_progress' | 'done' | 'cancelled';

export interface TaskMaterialRequestSummary {
  id: number;
  mr_no: string;
  status: 'pending' | 'fulfilled' | 'cancelled';
  material_type: 'raw_material' | 'offcut' | 'mixed';
  material_desc: string;
  require_qty: number;
  require_weight: number;
}

export interface MaterialRequestDetailItem {
  id?: number;
  source?: 'raw_material' | 'offcut';
  material_type?: 'raw_material' | 'offcut' | 'mixed';
  inventory_id?: number | null;
  offcut_stock_id?: number | null;
  bid?: string | null;
  ocsf?: string | null;
  locked_weight?: number | null;
  require_weight?: number | null;
  stock_weight?: number | null;
  avail_weight?: number | null;
  free_weight?: number | null;
  loc_code?: string | null;
  location?: string | null;
  location_code?: string | null;
  current_location?: string | null;
  current_loc?: string | null;
  batch_no?: string | null;
  inbound_batch_no?: string | null;
  inspection_no?: string | null;
  supplier_name?: string | null;
  supplier?: string | null;
  industry_grade?: string | null;
  parent_bid?: string | null;
  inbound_at?: string | null;
}

export interface MaterialRequestDetail extends TaskMaterialRequestSummary {
  plan_id?: number;
  plan_no?: string;
  task_id?: number;
  task_no?: string;
  customer_order_no?: string;
  material_no?: string;
  items?: MaterialRequestDetailItem[];
  material_items?: MaterialRequestDetailItem[];
  request_items?: MaterialRequestDetailItem[];
  inventory_locks?: MaterialRequestDetailItem[];
}

export interface ProductionTaskListItem {
  id: number;
  task_no: string;
  plan_id?: number;
  order_item_id?: number;
  task_qty: number;
  actual_qty?: number;
  planned_start_date?: string;
  planned_end_date?: string;
  status: ProductionTaskStatus;
  claimed_by_id?: number | null;
  claimed_by_name?: string;
  claimed_at?: string | null;
  started_at?: string | null;
  done_at?: string | null;
  cancel_reason?: string | null;
  remark?: string;
  plan_no?: string;
  order_id?: number;
  customer_order_no?: string;
  line_no?: number;
  customer_id?: number;
  customer_code?: string;
  customer_name?: string;
  customer_material_no?: string;
  material_desc?: string;
  order_qty?: number;
  material_request?: TaskMaterialRequestSummary | null;
  material_requests?: TaskMaterialRequestSummary[];
  created_at?: string;
  updated_at?: string;
}

export interface ProductionReportItem {
  id: number;
  reported_qty: number;
  remark?: string;
  reported_by_id?: number;
  reported_by_name?: string;
  reported_at?: string;
}

export interface ProductionTaskDetail extends ProductionTaskListItem {
  created_by_id?: number | null;
  cancelled_by?: number | null;
  cancelled_at?: string | null;
  reports?: ProductionReportItem[];
}

export interface ProductionTaskListResponse {
  list: ProductionTaskListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ProductionTaskListParams {
  page?: number;
  limit?: number;
  status_filter?: ProductionTaskStatus | 'all';
  claimed_by_filter?: 'me' | string;
  keyword?: string;
  order_item_id?: number;
  plan_id?: number;
}

export interface MaterialRequestListResponse {
  list: MaterialRequestDetail[];
  total: number;
  page?: number;
  limit?: number;
}

export interface OutboundOrderItemDetail {
  id: number;
  bid?: string;
  original_bid?: string;
  sku?: string;
  loc_code?: string;
  expected_weight?: number;
  outbound_weight?: number;
  residual_bid?: string;
  residual_weight?: number;
  scanned_at?: string;
}

export interface OutboundOrderDetail {
  id: number;
  order_no?: string;
  type?: string;
  type_name?: string;
  status?: string;
  status_name?: string;
  total_pieces?: number;
  scanned_pieces?: number;
  qr_payload?: string;
  items?: OutboundOrderItemDetail[];
}

export interface EmployeeHoldingSKUGroup {
  sku: string;
  pieces: number;
  total_weight: number;
  bids: string[];
}

export interface EmployeeHoldingDetail {
  user_id: number;
  username: string;
  full_name: string;
  emp_no: string;
  loc_code: string;
  total_pieces: number;
  total_weight: number;
  items: EmployeeHoldingSKUGroup[];
}

export type PmiScanType = 'task' | 'unit';

export interface PmiUnit {
  id?: number | string;
  inspection_serial: string;
  awaiting_spectrum?: boolean;
  bind_status?: string;
  position_no?: string;
  unit_no?: string;
  piece_no?: string;
  seq_no?: number | string;
  index?: number;
  grade?: string;
  material_grade?: string;
  steel_grade?: string;
  task_no?: string;
  pmi_task_no?: string;
  status?: string;
  is_bound?: boolean;
  has_spectrum?: boolean;
  bind_at?: string;
  bound_at?: string;
  spectrum_id?: number | string;
  spectrum_file_id?: number | string;
  spectrum_result_id?: number | string;
  replicate_id?: number | string | null;
  raw_status?: string | null;
  elements?: string | Record<string, number | string> | null;
  passed?: boolean | number | string | null;
  final_result?: string | null;
  measured_at?: string | null;
  serial?: string;
  serial_no?: string;
  [key: string]: unknown;
}

export interface PmiScanResult {
  scan_type: PmiScanType;
  current_unit: PmiUnit;
  units: PmiUnit[];
  task_no?: string;
  pmi_task_no?: string;
  grade?: string;
  material_grade?: string;
  total_units?: number;
  task?: {
    id?: number | string;
    task_no?: string;
    pmi_task_no?: string;
    grade?: string;
    material_grade?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface PmiSpectrumBindPayload {
  inspection_serial: string;
  file_name: string;
  sample_name?: string;
  instrument?: string;
  grade?: string;
  measured_at?: string;
  raw_status?: string;
  elements: Record<string, number | string>;
}

export interface PmiSpectrumBindResult {
  current_unit?: PmiUnit;
  units?: PmiUnit[];
  [key: string]: unknown;
}

export type FinalReleasePmiStatus = 'failed' | 'passed' | 'waiting' | string;

export interface FinalReleasePmiTraceScanResult {
  scan_type: 'trace' | string;
  trace_code: string;
  requires_pmi: boolean;
  pmi_status: FinalReleasePmiStatus;
  already_bound: boolean;
  can_retest: boolean;
  message?: string;
  grade?: string;
  material_grade?: string;
  raw_status?: string;
  elements?: Record<string, number | string> | Array<Record<string, unknown>>;
  element_results?: Array<Record<string, unknown>>;
  records?: Array<Record<string, unknown>>;
  trace?: {
    trace_code?: string | null;
    physical_piece_id?: number | string | null;
    [key: string]: unknown;
  } | null;
  trace_codes?: string[];
  units?: FinalReleasePmiInspectionUnit[];
  units_total?: number | string;
  units_pending?: number | string;
  [key: string]: unknown;
}

interface FinalReleasePmiSpectrumBindBasePayload {
  file_name?: string;
  sample_name?: string;
  method?: string;
  instrument?: string;
  grade?: string;
  measured_at: string;
  raw_status?: string;
  elements: Record<string, number>;
  reason?: string;
}

export type FinalReleasePmiSpectrumBindPayload = FinalReleasePmiSpectrumBindBasePayload & (
  | {
    trace_codes: string[];
    pmig_code?: string;
    pmig_codes?: never;
  }
  | {
    trace_codes?: never;
    pmig_code?: never;
    pmig_codes: string[];
  }
);

export interface FinalReleasePmiSpectrumBindResult {
  requirement_id: number;
  physical_piece_id: number;
  replicate_id: number;
  trace_codes: string[];
  pmig_codes: string[];
  bound_count: number;
  idempotent: boolean;
  passed_count: number;
  failed_count: number;
  final_result: string;
  units: PmiGroupBoundUnit[];
  failures: Array<Record<string, unknown>>;
  groups?: Array<{
    pmig_code: string;
    group_id: number | string;
    requirement_id: number | string;
    replicate_id: number | string;
    unit_count: number;
    passed: boolean;
  }>;
}

export type FinalReleasePmiRequirementStatus = 'pending' | 'completed' | 'cancelled';

export interface FinalReleasePmiInspectionUnit {
  id?: number | string;
  pmi_requirement_id?: number | string;
  unit_seq?: number | string | null;
  inspection_serial?: string | null;
  product_sn?: string | null;
  bind_status?: string | null;
  replicate_id?: number | string | null;
  replicate_no?: number | string | null;
  file_name?: string | null;
  grade?: string | null;
  raw_status?: string | null;
  elements?: string | Record<string, number | string> | null;
  passed?: boolean | number | string | null;
  final_result?: string | null;
  measured_at?: string | null;
  bound_at?: string | null;
  inspector_name?: string | null;
  [key: string]: unknown;
}

export interface FinalReleasePmiInspectionUnitListData {
  total?: number;
  items?: FinalReleasePmiInspectionUnit[];
  list?: FinalReleasePmiInspectionUnit[];
  data?: FinalReleasePmiInspectionUnit[];
}

export interface CustomerTechSpecElementItem {
  id?: number | string;
  element?: string | null;
  symbol?: string | null;
  code?: string | null;
  min_value?: number | string | null;
  max_value?: number | string | null;
  pmi_required?: boolean | number | string | null;
  pmi_min?: number | string | null;
  pmi_max?: number | string | null;
  [key: string]: unknown;
}

export interface CustomerTechSpecElementListData {
  elements?: CustomerTechSpecElementItem[];
  items?: CustomerTechSpecElementItem[];
  list?: CustomerTechSpecElementItem[];
  data?: CustomerTechSpecElementItem[];
}

export interface GeneratedDeliveryNoteLine {
  id?: number | string;
  line_id?: number | string;
  delivery_note_id?: number | string;
  order_no?: string | null;
  customer_order_no?: string | null;
  line_no?: number | string | null;
  material_no?: string | null;
  customer_material_no?: string | null;
  description?: string | null;
  material_desc?: string | null;
  customer_material_desc?: string | null;
  drawing_no?: string | null;
  quantity?: number | string | null;
  push_qty?: number | string | null;
  [key: string]: unknown;
}

export interface GeneratedDeliveryNote {
  id?: number | string;
  delivery_note_id?: number | string | null;
  note_no?: string | null;
  delivery_note_no?: string | null;
  pre_delivery_note_id?: number | string | null;
  pre_delivery_note_no?: string | null;
  customer_code?: string | null;
  customer_name?: string | null;
  purchase_group?: string | null;
  purchaser?: string | null;
  buyer?: string | null;
  total_quantity?: number | string | null;
  bound_pallet_quantity?: number | string | null;
  pallet_count?: number | string | null;
  pallet_info?: string | null;
  pallet_items?: Array<{
    description?: string | null;
    material_no?: string | null;
    materialNo?: string | null;
    quantity?: number | string | null;
    [key: string]: unknown;
  }> | null;
  palletItems?: Array<{
    description?: string | null;
    material_no?: string | null;
    materialNo?: string | null;
    quantity?: number | string | null;
    [key: string]: unknown;
  }> | null;
  pallet_quantity?: number | string | null;
  status?: string | null;
  status_text?: string | null;
  lines?: GeneratedDeliveryNoteLine[];
  items?: GeneratedDeliveryNoteLine[];
  [key: string]: unknown;
}

export interface GeneratedDeliveryNoteListParams {
  keyword?: string;
  note_no?: string;
  delivery_note_no?: string;
  page?: number;
  page_size?: number;
}

export interface GeneratedDeliveryNoteListData {
  items?: GeneratedDeliveryNote[];
  list?: GeneratedDeliveryNote[];
  data?: GeneratedDeliveryNote[];
  total?: number | string;
  page?: number | string;
  page_size?: number | string;
}

export interface DeliveryPalletStockItem {
  id?: number | string;
  material_no?: string | null;
  materialNo?: string | null;
  description?: string | null;
  stock_qty?: number | string | null;
  stockQty?: number | string | null;
  available_qty?: number | string | null;
  availableQty?: number | string | null;
  frozen_qty?: number | string | null;
  frozenQty?: number | string | null;
  reserved_qty?: number | string | null;
  reservedQty?: number | string | null;
  remark?: string | null;
  status?: string | null;
}

export interface DeliveryPalletStockListParams {
  page?: number;
  page_size?: number;
  material_no?: string;
  keyword?: string;
}

export interface DeliveryPalletStockListData {
  items?: DeliveryPalletStockItem[];
  list?: DeliveryPalletStockItem[];
  data?: DeliveryPalletStockItem[];
  total?: number | string;
  page?: number | string;
  page_size?: number | string;
}

export interface DeliveryNotePalletItemPayload {
  material_no: string;
  quantity: number;
}

export interface DeliveryNotePalletBindPayload {
  pallet_items: DeliveryNotePalletItemPayload[];
}

export type DeliveryLabelStatus = 'pending' | 'partial' | 'completed' | string;

export interface DeliveryLabelTaskNote {
  ID?: number;
  id?: number;
  note_no?: string;
  status?: string;
  total_qty?: number;
}

export interface DeliveryLabelTask {
  attached_label_count: number;
  blocking_reasons?: string[];
  can_create_songhuo?: boolean;
  exception_status?: 'clear' | 'pending' | string;
  expected_label_count: number;
  label_status: DeliveryLabelStatus;
  lines?: Array<Record<string, unknown>>;
  note?: DeliveryLabelTaskNote;
  pallet_status?: 'pending' | 'bound' | 'confirmed_none' | string;
  pmi_status?: 'not_required' | 'pending' | 'failed' | 'passed' | string;
  printed_label_count: number;
  remaining_label_count: number;
  task_code: string;
  task_qr_code?: string;
}

export interface DeliveryLabelTrace {
  attached_at?: string;
  attached_by_name?: string;
  delivery_note_id?: number;
  delivery_note_line_id?: number;
  item_index?: number;
  status?: string;
  trace_code: string;
}

export type DeliveryLabelTaskScanPayload = {
  code: string;
  confirm_attachment: false;
  enter_pmi: false;
  task_code?: never;
} | {
  code: string;
  confirm_attachment: true;
  enter_pmi: false;
  task_code: string;
};

export interface DeliveryLabelTaskScanData {
  attach: DeliveryLabelAttachmentReceipt | null;
  scope: 'task' | 'line' | 'label' | string;
  task: DeliveryLabelTask;
  trace?: DeliveryLabelTrace;
}

export interface DeliveryLabelAttachmentReceipt {
  attached_at: string;
  attached_by_id: number | string;
  attached_by_name: string;
  first_attachment: boolean;
  idempotent: boolean;
  label_event_id: number | string;
  print_instance_id: number | string;
  trace_code: string;
}

export interface PmiRebindPmig {
  code: string;
  delivery_requirement_detail_id?: number;
  id?: number;
  quantity: number;
  status: string;
}

export interface PmiRebindTarget {
  delivery_note_id?: number;
  item_index?: number;
  line_id?: number;
  represented_qty: number;
  trace_code: string;
}

export interface PmiRebindTaskSummary {
  can_create_songhuo?: boolean;
  pmi_status?: string;
  task_code?: string;
}

export type PmiRebindScanPayload = {
  code: string;
  pmig_code?: never;
} | {
  code: string;
  pmig_code: string;
};

export interface PmiRebindScanData {
  idempotent: boolean;
  message?: string;
  pmig: PmiRebindPmig;
  stage: 'awaiting_formal_label' | 'completed' | string;
  swapped: boolean;
  target?: PmiRebindTarget;
  task?: PmiRebindTaskSummary;
}

export interface PmiGroupSummary {
  delivery_requirement_detail_id?: number;
  delivery_requirement_id?: number;
  group_code: string;
  id: number;
  pmi_requirement_id?: number;
  print_status: 'not_printed' | 'printed' | string;
  quantity: number;
  status: 'pending' | 'awaiting' | 'completed' | 'voided' | string;
}

export interface PmiGroupRequirementSummary {
  customer_code?: string;
  customer_spec_no?: string;
  customer_tech_spec_id?: number | string;
  id?: number;
  line_no?: string;
  material_grade?: string;
  order_no?: string;
  pmi_inspection?: string | null;
}

export interface PmiGroupScanResult {
  already_bound?: boolean;
  can_rebind?: boolean;
  group: PmiGroupSummary;
  has_pmi_data?: boolean;
  message?: string;
  pmig_code: string;
  pmi_status: string;
  requirement?: PmiGroupRequirementSummary;
  requires_pmi?: boolean;
  scan_type: 'group';
  task_code?: string;
  units: PmiUnit[];
  units_pending?: number;
  units_total?: number;
}

export interface PmiGroupSpectrumBindPayload {
  elements: Record<string, number>;
  file_name?: string;
  grade?: string;
  instrument?: string;
  measured_at: string;
  method?: string;
  pmig_code: string;
  raw_status?: string;
  reason?: string;
  sample_name?: string;
}

export interface PmiGroupBoundUnit {
  bind_status?: string;
  fail_reason?: string;
  final_result?: string;
  id?: number | string;
  inspection_serial?: string;
  passed?: boolean | number | string;
  replicate_id?: number | string;
  [key: string]: unknown;
}

export interface PmiGroupSpectrumBindResult {
  failed_count?: number;
  failures?: Array<Record<string, unknown>> | null;
  final_result?: string;
  passed_count?: number;
  replicate_id?: number | string;
  row?: Record<string, unknown>;
  unit?: PmiGroupBoundUnit;
  units?: PmiGroupBoundUnit[];
}

export type MyPmiDetectionAction = 'bind' | 'rebind' | 'overwrite' | 'partial_rebind';

export type MyPmiDetectionResultFilter = 'pass' | 'fail';

export interface MyPmiDetectionHistoryParams {
  end_at?: string;
  page?: number;
  page_size?: number;
  result?: MyPmiDetectionResultFilter;
  start_at?: string;
}

export interface MyPmiDetectionHistoryItem {
  action: MyPmiDetectionAction;
  customer_code: string;
  detail: string;
  detected_at: string;
  id: number;
  inspection_serial: string;
  material_grade: string;
  order_no: string;
  passed: boolean | null;
  trace_code: string;
}

export interface MyPmiDetectionHistoryData {
  fail_count: number;
  items: MyPmiDetectionHistoryItem[];
  page: number;
  page_size: number;
  pass_count: number;
  total: number;
}

export interface MyPmiDetectionHistoryElement {
  element: string;
  passed: boolean | null;
  pmi_max: number | string | null;
  pmi_min: number | string | null;
  reason: string;
  required: boolean;
  value: number | string | null;
}

export interface MyPmiDetectionHistoryDetail extends MyPmiDetectionHistoryItem {
  elements: MyPmiDetectionHistoryElement[];
  fail_reason: string | null;
  file_name: string | null;
  grade: string | null;
  instrument: string | null;
  measured_at: string | null;
  raw_status: string | null;
  replicate_id: number | string | null;
  replicate_no: number | string | null;
  sample_name: string | null;
}

export interface FinalReleasePmiRequirement {
  id?: number | string;
  pmi_requirement_id?: number | string | null;
  requirement_id?: number | string | null;
  inspection_batch_no?: string | null;
  delivery_requirement_id?: number | string | null;
  upload_id?: number | string | null;
  customer_code?: string | null;
  customer_spec_no?: string | null;
  customer_tech_spec_id?: number | string | null;
  material_grade?: string | null;
  order_no?: string | null;
  line_no?: string | null;
  item_no?: string | null;
  material_no?: string | null;
  product_description?: string | null;
  drawing_no?: string | null;
  order_quantity?: number | string | null;
  material?: string | null;
  current_quantity?: number | string | null;
  material_requirement?: string | null;
  pmi_inspection?: string | null;
  passed_count?: number | string | null;
  failed_count?: number | string | null;
  pmi_data?: string | null;
  operator?: string | null;
  inspection_date?: string | null;
  sy_no?: string | null;
  pmi_task_id?: number | string | null;
  inspection_plan_id?: number | string | null;
  inspection_item_id?: number | string | null;
  status?: FinalReleasePmiRequirementStatus | string | null;
  source?: string | null;
  remark?: string | null;
}

export interface FinalReleasePmiRequirementListParams {
  keyword?: string;
  customer_code?: string;
  status?: FinalReleasePmiRequirementStatus | string;
  order_no?: string;
  sy_no?: string;
  page?: number;
  page_size?: number;
}

export interface FinalReleasePmiRequirementListData {
  total?: number;
  page?: number;
  page_size?: number;
  items?: FinalReleasePmiRequirement[];
  list?: FinalReleasePmiRequirement[];
  data?: FinalReleasePmiRequirement[];
}

export function cardLogin(cardCode: string) {
  return request.post('/warehouse/auth/card-login', {
    card_code: cardCode,
  }) as Promise<{ data: PdaUser }>;
}

export function listMyCompanies() {
  return request.get('/users/my-companies') as Promise<{ data: MyCompaniesData }>;
}

export function switchOrganization(companyId: number) {
  return request.post('/users/switch-org', {
    company_id: companyId,
  }) as Promise<{ data: SwitchOrganizationData }>;
}

export function scanOffcutInbound(payload: ScanInboundPayload) {
  return request.post(
    '/warehouse/offcut-stocks/scan/inbound',
    payload,
  ) as Promise<{ data: ScanInboundResult }>;
}

export function scanOffcutInboundBatch(payload: ScanInboundBatchPayload) {
  return request.post(
    '/warehouse/offcut-stocks/scan/inbound-batch',
    payload,
  ) as Promise<{ data: ScanInboundResult }>;
}

export function scanOffcutOutbound(payload: ScanOutboundPayload) {
  return request.post(
    '/warehouse/offcut-stocks/scan/outbound',
    payload,
  ) as Promise<{ data: ScanOutboundResult }>;
}

export function scanOffcutTransfer(payload: ScanTransferPayload) {
  return request.post(
    '/warehouse/offcut-stocks/scan/transfer',
    payload,
  ) as Promise<{ data: ScanTransferResult }>;
}

export function getOffcutByCode(ocsf: string) {
  return request.get(
    `/warehouse/offcut-stocks/by-code/${encodeURIComponent(ocsf)}`,
  ) as Promise<{ data: OffcutTraceResult }>;
}

export function getInventoryTraceByBid(bid: string) {
  return request.get(
    `/warehouse/inventory/${encodeURIComponent(bid)}/trace`,
  ) as Promise<{ data: InventoryTraceResponse }>;
}

export function getPreInboundScanContext(id: number | string) {
  return request.get(
    `/warehouse/pre-inbounds/${encodeURIComponent(String(id))}/scan-prepare`,
  ) as Promise<{ data: PreInboundScanContext }>;
}

export function getPreInboundDetail(id: number | string) {
  return request.get(
    `/warehouse/pre-inbounds/${encodeURIComponent(String(id))}`,
  ) as Promise<{ data: PreInboundDetail }>;
}

export function scanAddPreInboundItem(
  id: number | string,
  payload: PreInboundScanAddPayload,
) {
  return request.post(
    `/warehouse/pre-inbounds/${encodeURIComponent(String(id))}/scan-add`,
    payload,
  ) as Promise<{ data: PreInboundScanAddResult }>;
}

export function getProductionTasks(params?: ProductionTaskListParams) {
  return request.get('/production/tasks', {
    params,
  }) as Promise<{ data: ProductionTaskListResponse }>;
}

export function getProductionTaskDetail(id: number | string) {
  return request.get(
    `/production/tasks/${encodeURIComponent(String(id))}`,
  ) as Promise<{ data: ProductionTaskDetail }>;
}

export function listMaterialRequests(params?: {
  page?: number;
  limit?: number;
  status?: string;
  keyword?: string;
}) {
  return request.get('/production/material-requests', {
    params,
  }) as Promise<{ data: MaterialRequestListResponse }>;
}

export function getMaterialRequestDetail(id: number | string) {
  return request.get(
    `/production/material-requests/${encodeURIComponent(String(id))}`,
  ) as Promise<{ data: MaterialRequestDetail }>;
}

export function getOutboundOrderDetail(id: number | string) {
  return request.get(
    `/warehouse/outbound-orders/${encodeURIComponent(String(id))}`,
  ) as Promise<{ data: OutboundOrderDetail }>;
}

export function getMyHolding() {
  return request.get(
    '/warehouse/employee-holdings/me',
  ) as Promise<{ data: EmployeeHoldingDetail }>;
}

export function scanPmiCode(code: string) {
  return request.post('/quality/final-release/pmi/scan', {
    code,
  }) as Promise<{ data: PmiScanResult }>;
}

export function bindPmiSpectrum(payload: PmiSpectrumBindPayload) {
  return request.post(
    '/quality/final-release/pmi/spectrum-bind',
    payload,
  ) as Promise<{ data: PmiSpectrumBindResult }>;
}

export function scanFinalReleasePmiTrace(code: string) {
  return request.post('/quality/final-release/pmi/scan', {
    code,
  }) as Promise<{ data: FinalReleasePmiTraceScanResult }>;
}

export function bindFinalReleasePmiSpectrum(payload: FinalReleasePmiSpectrumBindPayload) {
  return request.post(
    '/quality/final-release/pmi/spectrum-bind',
    payload,
  ) as Promise<{ data: FinalReleasePmiSpectrumBindResult; msg?: string }>;
}

export function listFinalReleasePmiRequirements(params?: FinalReleasePmiRequirementListParams) {
  return request.get('/quality/final-release/pmi-requirements', {
    params,
  }) as Promise<{ data: FinalReleasePmiRequirementListData }>;
}

export function listFinalReleasePmiRequirementUnits(id: number | string) {
  return request.get(
    `/quality/final-release/pmi-requirements/${encodeURIComponent(String(id))}/units`,
  ) as Promise<{ data: FinalReleasePmiInspectionUnitListData }>;
}

export function listCustomerTechSpecElements(id: number | string) {
  return request.get(
    `/quality/customer-tech-specs/${encodeURIComponent(String(id))}/elements`,
  ) as Promise<{ data: CustomerTechSpecElementItem[] | CustomerTechSpecElementListData }>;
}

export function listGeneratedDeliveryNotes(params?: GeneratedDeliveryNoteListParams) {
  return request.get('/delivery-notes', {
    params,
  }) as Promise<{ data: GeneratedDeliveryNoteListData | GeneratedDeliveryNote[] }>;
}

export function listDeliveryPalletStocks(params?: DeliveryPalletStockListParams) {
  return request.get('/delivery-pallet-stocks', {
    params,
  }) as Promise<{ data: DeliveryPalletStockListData | DeliveryPalletStockItem[] }>;
}

export function bindGeneratedDeliveryNotePallets(
  id: number | string,
  payload: DeliveryNotePalletBindPayload,
) {
  return request.patch(
    `/delivery-notes/${encodeURIComponent(String(id))}/pallet`,
    payload,
  ) as Promise<{ data?: unknown }>;
}

export function scanDeliveryLabelTask(payload: DeliveryLabelTaskScanPayload) {
  return request.post(
    '/delivery-notes/task-scan',
    payload,
  ) as Promise<{ data: DeliveryLabelTaskScanData; msg?: string }>;
}

export function getDeliveryLabelTask(id: number | string) {
  return request.get(
    `/delivery-notes/${encodeURIComponent(String(id))}/task`,
  ) as Promise<{ data: DeliveryLabelTask }>;
}

export function scanPmiRebind(payload: PmiRebindScanPayload) {
  return request.post(
    '/delivery-labels/pmi-rebind/scan',
    payload,
  ) as Promise<{ data: PmiRebindScanData; msg?: string }>;
}

export function scanPmiGroup(code: string) {
  return request.post('/quality/final-release/pmi/scan', {
    code,
  }) as Promise<{ data: PmiGroupScanResult; msg?: string }>;
}

export function bindPmiGroupSpectrum(payload: PmiGroupSpectrumBindPayload) {
  return request.post(
    '/quality/final-release/pmi/spectrum-bind',
    payload,
  ) as Promise<{ data: PmiGroupSpectrumBindResult; msg?: string }>;
}

export function getMyPmiDetectionHistory(params?: MyPmiDetectionHistoryParams) {
  return request.get('/quality/final-release/pmi/my-detections', {
    params,
  }) as Promise<{ data: MyPmiDetectionHistoryData; msg?: string }>;
}

export function getMyPmiDetectionHistoryDetail(id: number | string) {
  return request.get(
    `/quality/final-release/pmi/my-detections/${encodeURIComponent(String(id))}`,
  ) as Promise<{ data: MyPmiDetectionHistoryDetail; msg?: string }>;
}

export function claimProductionTask(id: number | string) {
  return request.post(
    `/production/tasks/${encodeURIComponent(String(id))}/claim`,
  ) as Promise<{ data?: unknown }>;
}
